import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { FaLeaf, FaApple } from 'react-icons/fa'
import { FcGoogle } from 'react-icons/fc'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import usePostData from '../../customhooks/usePostData'
import { mergeGuestCart } from '../../feature/cartSlice'
import sideImg from '../../assets/40883aa1-a1d5-4c5c-ab2e-89acf9b1646e.png'

const inputCls =
  'w-full h-[44px] border border-[#dfe7d9] bg-white px-4 text-[13px] text-[#1f2d22] rounded-md outline-none focus:border-[#2f6b3a] transition-colors placeholder:text-[#9aa79c]'

const labelCls = 'block mb-1.5 text-[11px] text-[#6b7a6d]'

const primaryBtn =
  'w-full h-[46px] mt-1 bg-[#25663a] hover:bg-[#1a4a29] disabled:opacity-60 transition-colors text-white text-[14px] font-semibold rounded-full'

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const baseurl = process.env.REACT_APP_BASE_API
  const { postData } = usePostData(`${baseurl}/users/signup`)

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (submitting) return // ignore double clicks

    if (!formData.name || !formData.phone || !formData.email || !formData.password) {
      setError('Please fill in all fields.')
      return
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    if (!agreed) {
      setError('Please accept the Terms & Conditions and Privacy Policy.')
      return
    }

    setSubmitting(true)
    try {
      const response = await postData(formData)
      if (response.token) {
        localStorage.setItem('token', response.token)
        if (response.user) localStorage.setItem('user', JSON.stringify(response.user))

        // move the guest cart into the new account's cart, then continue
        await dispatch(mergeGuestCart())
        navigate(location.state?.from || '/', { replace: true })
      } else if (response.message) {
        setError(response.message)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  // Google / Apple signup need their own setup on your backend
  const socialSignup = (provider) => {
    setError(`${provider} signup is not connected yet.`)
  }

  return (
    <div className="min-h-screen bg-[#eef2e8] flex items-center justify-center mt-[50px] px-4 py-8">
      <div className="w-full max-w-[940px] bg-[#f8faf3] border border-[#e3e9dd] shadow-xl rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-[1.6fr_1fr]">
        {/* ============ LEFT: FORM ============ */}
        <div className="px-6 sm:px-12 py-8 sm:py-10">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center gap-2.5 mb-6">
            <span className="w-[42px] h-[42px] rounded-full bg-white border border-[#cfe0cb] flex items-center justify-center">
              <FaLeaf className="text-[18px] text-[#2f6b3a]" />
            </span>
            <span className="text-left">
              <span className="block font-serif text-[26px] text-[#1f4a2b] leading-none">
                Jeetayu
              </span>
              <span className="block text-[7px] tracking-[1.6px] text-[#6b7a6d] mt-1">
                AYURVEDA FOR A BETTER LIFE
              </span>
            </span>
          </Link>

          <h2 className="font-serif font-bold text-[26px] sm:text-[30px] text-center text-[#1f4a2b] mb-1.5">
            Create Your Account
          </h2>
          <p className="text-center text-[12px] text-[#4b574d] mb-6">
            Join Jeetayu for exclusive offers, wellness tips and more.
          </p>

          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <div>
              <label htmlFor="name" className={labelCls}>
                Full Name
              </label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                autoComplete="name"
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="phone" className={labelCls}>
                Mobile Number
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 98765 43210"
                autoComplete="tel"
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="email" className={labelCls}>
                Email Address
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                autoComplete="email"
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="password" className={labelCls}>
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Password"
                  autoComplete="new-password"
                  className={`${inputCls} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b7a6d] hover:text-[#1f4a2b]"
                >
                  {showPassword ? (
                    <FiEyeOff className="text-[16px]" />
                  ) : (
                    <FiEye className="text-[16px]" />
                  )}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 text-[11px] text-[#4b574d]">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => {
                  setAgreed(e.target.checked)
                  setError('')
                }}
                className="h-3.5 w-3.5 rounded border-[#dfe7d9] accent-[#2f6b3a]"
              />
              <span>
                I agree to the{' '}
                <Link to="/terms" className="text-[#25663a] hover:underline">
                  Terms &amp; Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-[#25663a] hover:underline">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            {error && (
              <p role="alert" className="text-[13px] text-red-500">
                {error}
              </p>
            )}

            <button type="submit" disabled={submitting} className={primaryBtn}>
              {submitting ? 'Creating account...' : 'Sign Up'}
            </button>
          </form>

          <p className="text-center text-[11px] text-[#6b7a6d] my-5">Or continue with</p>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => socialSignup('Google')}
              className="h-[42px] flex items-center justify-center gap-2 bg-white border border-[#dfe7d9] rounded-md text-[12px] font-medium text-[#1f2d22] hover:bg-[#f2f6ec] transition-colors"
            >
              <FcGoogle className="text-[17px]" /> Google
            </button>
            <button
              type="button"
              onClick={() => socialSignup('Apple')}
              className="h-[42px] flex items-center justify-center gap-2 bg-white border border-[#dfe7d9] rounded-md text-[12px] font-medium text-[#1f2d22] hover:bg-[#f2f6ec] transition-colors"
            >
              <FaApple className="text-[17px] text-black" /> Apple
            </button>
          </div>

          <p className="text-center text-[11px] text-[#4b574d] mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#25663a] font-semibold hover:underline">
              Login
            </Link>
          </p>
        </div>

        {/* ============ RIGHT: IMAGE PANEL ============ */}
        <div className="relative hidden md:block min-h-[560px] bg-[#f1f4e8] overflow-hidden">
          <img
            src={sideImg}
            alt=""
            aria-hidden="true"
            className="absolute bottom-0 left-0 w-full h-[68%] object-cover object-center"
            style={{
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, #000 30%)',
              maskImage: 'linear-gradient(to bottom, transparent 0%, #000 30%)',
            }}
          />

          <FaLeaf className="absolute top-3 left-3 text-[34px] text-[#9fc58f]/70 -rotate-45" />
          <FaLeaf className="absolute top-2 right-3 text-[40px] text-[#8fba7e]/70 rotate-45" />
        </div>
      </div>
    </div>
  )
}

export default Signup
